import { useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * Movimentação ainda NÃO existe na API (`apps/api/src/routes/index.ts` não
 * registra nenhuma rota /api/movimentos). Os hooks abaixo já estão escritos
 * no formato que a rota deve ter, mas ficam desligados por
 * `API_MOVIMENTOS_PRONTA = false`: nenhuma requisição é disparada e nenhum
 * dado de mentira é devolvido. Quando a rota existir, basta virar a
 * constante — a UI não muda.
 */
export const API_MOVIMENTOS_PRONTA = false;

export type SituacaoMovimento = 'pendente' | 'confirmado' | 'cancelado';

export interface Movimento {
  id: number;
  criadoEm: string;
  situacao: SituacaoMovimento;
  produto: number;
  produtoDescricao: string;
  quantidade: number;
  origemLocalizador: number;
  origemEndereco: string;
  destinoLocalizador: number;
  destinoEndereco: string;
  solicitante: string;
}

export interface NovoMovimento {
  centro: number;
  produto: number;
  quantidade: number;
  origemLocalizador: number;
  destinoLocalizador: number;
}

export type AbaMovimentos = 'pendentes' | 'hoje' | 'historico';

export function useMovimentos(
  centro: number | null,
  aba: AbaMovimentos,
): UseQueryResult<Movimento[], unknown> {
  return useQuery<Movimento[]>({
    queryKey: ['movimentos', centro, aba],
    queryFn: () => api.get<Movimento[]>('/api/movimentos', { centro, situacao: aba }),
    enabled: API_MOVIMENTOS_PRONTA && centro !== null,
  });
}

export function useCriarMovimento() {
  return useMutation<Movimento, unknown, NovoMovimento>({
    mutationFn: (dados) => {
      if (!API_MOVIMENTOS_PRONTA) {
        return Promise.reject(new Error('A API de movimentação ainda não foi implementada.'));
      }
      return api.post<Movimento>('/api/movimentos', dados);
    },
  });
}

export function useConfirmarMovimento() {
  return useMutation<Movimento, unknown, { id: number }>({
    mutationFn: ({ id }) => {
      if (!API_MOVIMENTOS_PRONTA) {
        return Promise.reject(new Error('A API de movimentação ainda não foi implementada.'));
      }
      return api.post<Movimento>(`/api/movimentos/${id}/confirmar`);
    },
  });
}
